import { useState } from 'react';
import { CURRENCIES, EXPENSE_CATEGORIES, FEE_STATUSES,
  type ExpenseDTO, type ExpenseCategory, type ShowFeeDTO, type FeeStatus } from '../api';
import { useTranslation } from 'react-i18next';
import { withCardIcon } from '../lib/uiIcons';

/**
 * FinanceTab — the "Finance" tab of the Show detail page: the deal (fee,
 * deposit, payment, per diem), the expenses list, and the per-person musician
 * fees (cachets) with a per-currency settlement. Amounts are never converted
 * between currencies.
 *
 * Extracted from ShowDetailPage (2026-08-14, Vague 3 pass 6). Props-driven:
 * the parent owns `form` (show fields), expenses + fees state, and mutation
 * callbacks.
 *
 * Roadcase (P4c): chrome harmonised to the `.rc-*` language — `.rc-card-head`
 * + mono uppercase `.rc-header-title` (+ `.rc-header-sub`), the deal block is
 * a `.rc-field-grid` of stacked `form-field rc-form-row col` cells (2 columns,
 * 1 under 768px), the managed rows (expenses / fees) are `.rc-list` of
 * `.rc-card col` whose controls cluster in `.rc-card-fields`, sub-headings are
 * `.rc-section-title` and the settlement is a `.rc-card col` summary. ZERO
 * logic touched: every prop, handler, capability gate (`canFees` /
 * `canExpenses` / `canWriteShow`) and the never-converted amount rules are the
 * pre-P4c state, byte for byte.
 */
export interface FinanceTabProps {
  form: Record<string, any> & { guarantee?: number | null; currency?: string | null; fee_type?: string | null; deposit_amount?: number | null; deposit_received?: number | null; payment_status?: string | null; payment_due_date?: string | null; payment_notes?: string | null; per_diem_amount?: number | null; per_diem_people?: number | null };
  setField: (k: string, v: any) => void;
  expenses: ExpenseDTO[];
  addExpense: () => void;
  updateExpense: (x: ExpenseDTO, patch: Partial<ExpenseDTO>) => void;
  removeExpense: (id: number) => void;
  perDiemTotal: number;
  expensesByCurrency: Record<string, number>;
  netInDealCurrency: number;
  /** Fallback currency when the show has none set (the band's home currency). */
  defaultCurrency?: string;
  /**
   * Capability `finance.expenses` — show expenses (chantier C, 2026-09-25).
   * Defaults to true so an existing call site keeps today's behaviour.
   */
  canExpenses?: boolean;
  /**
   * Capability `finance.fees` — the show deal + the per-person musician fees
   * (cachets) and the settlement derived from them. Defaults to true.
   */
  canFees?: boolean;
  /**
   * Lot A2: may the DEAL fields (fee, currency, fee type, deposit, payment
   * status/due date, per diem) be written? They are SHOW-record columns (they
   * live on `tour_dates`, the server accepts them in `FIELDS`), so their write
   * path is `PUT /api/shows/:id` — mounted AFTER the server's global read-only
   * guard → 403 for a `users.role = 'read-only'` account. Distinct from
   * `canFees`, which also opens the per-person fees list: those go through
   * `/api/shows/:id/fees`, a DELEGATED family mounted BEFORE the guard
   * (`finance.fees`). Hence the separate flag: a read-only account can
   * legitimately hold `canFees` (and the fees rows stay writable) while the deal
   * block must stay read-only. Defaults to true (existing call sites unchanged).
   *
   * Known pre-existing gap (NOT introduced here, reported): the page's Save
   * payload and its core autosave both omit these columns, so the deal block is
   * currently inert for EVERY account. Freezing it for a read-only account is
   * therefore lossless today — and becomes load-bearing the moment the deal write
   * is wired through `PUT /api/shows/:id`.
   */
  canWriteShow?: boolean;
  // ---- Musician fees / cachets (per member × show) ----
  /** The show's fees (one row per person). */
  fees: ShowFeeDTO[];
  /** Fee totals grouped by currency (never converted). */
  feesByCurrency: Record<string, number>;
  /** Candidate members for a new fee (the band's team). */
  showMembers: { id: string; name: string }[];
  /** Create a fee for one member (upsert — defaults filled by the parent). */
  addFee: (memberId: string) => void;
  /** Update one fee (optimistic). */
  updateFee: (fee: ShowFeeDTO, patch: Partial<ShowFeeDTO>) => void;
  /** Delete one fee. */
  removeFee: (id: number) => void;
  /** Apply the SAME fee to several members, or every active member on the show. */
  bulkFees: (data: { all_on_show?: boolean; team_member_ids?: string[]; amount: number; currency: string }) => void;
}

export default function FinanceTab({
  form,
  setField,
  expenses,
  addExpense,
  updateExpense,
  removeExpense,
  perDiemTotal,
  expensesByCurrency,
  netInDealCurrency,
  defaultCurrency = 'EUR',
  canExpenses = true,
  canFees = true,
  canWriteShow = true,
  fees,
  feesByCurrency,
  showMembers,
  addFee,
  updateFee,
  removeFee,
  bulkFees,
}: FinanceTabProps) {
  const { t } = useTranslation();
  // A show's deal currency, falling back to the band's home currency.
  const showCurrency = form.currency || defaultCurrency;
  // Picker state for a new fee + the "apply to all" bulk amount/currency.
  const [pickMember, setPickMember] = useState('');
  const [bulkAmount, setBulkAmount] = useState<string>('');
  const [bulkCurrency, setBulkCurrency] = useState(showCurrency);
  // Members already having a fee are excluded from the picker (one per person).
  const feeMemberIds = new Set(fees.map((f) => f.team_member_id));
  const availableMembers = showMembers.filter((m) => !feeMemberIds.has(m.id));
  return (
    <div className="card" data-finance-tab style={{ maxWidth: 1000, marginBottom: 20 }}>
      <div className="rc-card-head">
        <div>
          <h3 className="rc-header-title">{withCardIcon('show_finance:finance', t('finance.title'))}</h3>
          <p className="rc-header-sub">{t('finance.subtitle')}</p>
        </div>
      </div>

      {/* Deal (fee, deposit, payment, per diem) — the show's own fee/cachet,
          gated by the `finance.fees` capability. The 10 columns sit in the
          day-sheet field grid (2 columns, 1 under 768px). */}
      {canFees && (<>
      <div className="rc-field-grid" data-finance="deal">
        <div className="form-field rc-form-row col">
          <label>{t('finance.fee')}</label>
          <input className="form-input" type="number" value={form.guarantee ?? 0}
            disabled={!canWriteShow}
            onChange={(e) => setField('guarantee', e.target.value === '' ? 0 : Number(e.target.value))} />
        </div>
        <div className="form-field rc-form-row col">
          <label>{t('finance.currency')}</label>
          <select className="form-input" value={showCurrency}
            disabled={!canWriteShow}
            onChange={(e) => setField('currency', e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-field rc-form-row col">
          <label>{t('finance.feeType')}</label>
          <select className="form-input" value={form.fee_type || 'guarantee'}
            disabled={!canWriteShow}
            onChange={(e) => setField('fee_type', e.target.value)}>
            <option value="guarantee">{t('finance.guarantee')}</option>
            <option value="door deal">{t('finance.doorDeal')}</option>
            <option value="split">{t('finance.split')}</option>
            <option value="buyout">{t('finance.buyout')}</option>
            <option value="free">{t('finance.free')}</option>
          </select>
        </div>
        <div className="form-field rc-form-row col">
          <label>{t('finance.deposit')}</label>
          <input className="form-input" type="number" value={form.deposit_amount ?? 0}
            disabled={!canWriteShow}
            onChange={(e) => setField('deposit_amount', e.target.value === '' ? 0 : Number(e.target.value))} />
        </div>
        <div className="form-field rc-form-row col" style={{ justifyContent: 'flex-end' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={!!form.deposit_received}
              disabled={!canWriteShow}
              onChange={(e) => setField('deposit_received', e.target.checked ? 1 : 0)} />
            {t('finance.received')}
          </label>
        </div>
        <div className="form-field rc-form-row col">
          <label>{t('finance.payment')}</label>
          <select className="form-input" value={form.payment_status || 'unpaid'}
            disabled={!canWriteShow}
            onChange={(e) => setField('payment_status', e.target.value)}>
            <option value="unpaid">{t('finance.unpaid')}</option>
            <option value="partial">{t('finance.partial')}</option>
            <option value="paid">{t('finance.paid')}</option>
            <option value="overdue">{t('finance.overdue')}</option>
          </select>
        </div>
        <div className="form-field rc-form-row col">
          <label>{t('finance.dueDate')}</label>
          <input className="form-input" type="date" value={(form.payment_due_date || '').slice(0, 10)}
            disabled={!canWriteShow}
            onChange={(e) => setField('payment_due_date', e.target.value)} />
        </div>
        {/* `payment_notes` — the 10ᵉ colonne du deal. Elle partait déjà dans le Save
            payload et l'autosave (`PUT /api/shows/:id`, champ `tour_dates`) mais
            AUCUN contrôle ne l'exposait : la colonne ne pouvait pas être saisie.
            Même gate que les autres champs du bloc (show-level `canWriteShow`). */}
        <div className="form-field rc-form-row col">
          <label>{t('finance.paymentNotes')}</label>
          <input className="form-input" type="text" value={form.payment_notes || ''}
            disabled={!canWriteShow}
            onChange={(e) => setField('payment_notes', e.target.value)} />
        </div>
        <div className="form-field rc-form-row col">
          <label>{t('finance.perDiemPerson')}</label>
          <input className="form-input" type="number" value={form.per_diem_amount ?? 0}
            disabled={!canWriteShow}
            onChange={(e) => setField('per_diem_amount', e.target.value === '' ? 0 : Number(e.target.value))} />
        </div>
        <div className="form-field rc-form-row col">
          <label>{t('finance.people')}</label>
          <input className="form-input" type="number" value={form.per_diem_people ?? 0}
            disabled={!canWriteShow}
            onChange={(e) => setField('per_diem_people', e.target.value === '' ? 0 : Number(e.target.value))} />
        </div>
        <div className="rc-form-row" style={{ gridColumn: '1 / -1', color: 'var(--text-dim)' }}>
          {t('finance.total', { total: perDiemTotal.toLocaleString(), currency: showCurrency })}
        </div>
      </div>
      </>)}

      {/* Expenses — `finance.expenses` capability. */}
      {canExpenses && (<>
      <div data-finance="expenses">
        <h4 className="rc-section-title" style={{ margin: '16px 0 10px' }}>{t('finance.expenses')}</h4>
        <div className="rc-list" data-finance-expenses>
          {expenses.map((x) => (
            <div key={x.id} className="rc-card col" data-finance-expense>
              <div className="rc-card-fields">
                <select className="form-input" style={{ flex: '0 1 130px', minWidth: 0 }} value={x.category}
                  onChange={(e) => updateExpense(x, { category: e.target.value as ExpenseCategory })}>
                  {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input className="form-input" style={{ flex: '1 1 160px', minWidth: 0 }} placeholder={t('finance.label')}
                  value={x.label || ''} onChange={(e) => updateExpense(x, { label: e.target.value })} />
                <input className="form-input" style={{ flex: '0 1 110px', minWidth: 0 }} type="number" placeholder="0"
                  value={x.amount ?? 0} onChange={(e) => updateExpense(x, { amount: Number(e.target.value) || 0 })} />
                <select className="form-input" style={{ flex: '0 1 90px', minWidth: 0 }} value={x.currency || showCurrency}
                  onChange={(e) => updateExpense(x, { currency: e.target.value })}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: '0.85rem', flex: '0 0 auto' }}>
                  <input type="checkbox" checked={!!x.paid}
                    onChange={(e) => updateExpense(x, { paid: e.target.checked ? 1 : 0 })} />
                  {t('finance.paidCheck')}
                </label>
                <button className="btn btn-small btn-danger" style={{ flex: '0 0 auto', marginLeft: 'auto' }} onClick={() => removeExpense(x.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
        <button className="btn btn-small btn-secondary" style={{ marginTop: 10 }} onClick={addExpense}>
          {t('finance.addExpense')}
        </button>
      </div>
      </>)}

      {/* Musician fees / cachets — `finance.fees` capability, one structured
          fee per person on this show. */}
      {canFees && (<>
      <div data-finance="fees">
        <h4 className="rc-section-title" style={{ margin: '18px 0 4px' }}>{t('fees.title')}</h4>
        <p style={{ color: 'var(--text-dim)', margin: '0 0 8px', fontSize: '0.85rem' }}>{t('fees.subtitle')}</p>
        <div className="rc-list" data-finance-fees>
          {fees.length === 0 && <span style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>{t('fees.empty')}</span>}
          {fees.map((f) => (
            <div key={f.id} className="rc-card col" data-fee-row>
              <div className="rc-card-fields">
                <span className="rc-card-name" style={{ flex: '1 1 140px', minWidth: 0 }}>{f.member_name || f.team_member_id}</span>
                <input className="form-input" style={{ flex: '0 1 110px', minWidth: 0 }} type="number" placeholder="0"
                  value={f.amount ?? 0} onChange={(e) => updateFee(f, { amount: Number(e.target.value) || 0 })} />
                <select className="form-input" style={{ flex: '0 1 90px', minWidth: 0 }} value={f.currency || showCurrency}
                  onChange={(e) => updateFee(f, { currency: e.target.value })}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select className="form-input" style={{ flex: '0 1 110px', minWidth: 0 }} value={f.status}
                  onChange={(e) => updateFee(f, { status: e.target.value as FeeStatus })}>
                  {FEE_STATUSES.map((s) => <option key={s} value={s}>{t(`fees.status.${s}`)}</option>)}
                </select>
                <input className="form-input" style={{ flex: '0 1 100px', minWidth: 0 }} type="number" placeholder="0"
                  title={t('fees.paidAmount')} value={f.paid_amount ?? 0}
                  onChange={(e) => updateFee(f, { paid_amount: Number(e.target.value) || 0 })} />
                <input className="form-input" style={{ flex: '0 1 140px', minWidth: 0 }} type="date"
                  title={t('fees.paidDate')} value={(f.paid_date || '').slice(0, 10)}
                  onChange={(e) => updateFee(f, { paid_date: e.target.value })} />
                <input className="form-input" style={{ flex: '1 1 150px', minWidth: 0 }} placeholder={t('fees.notes')}
                  value={f.notes || ''} onChange={(e) => updateFee(f, { notes: e.target.value })} />
                <button className="btn btn-small btn-danger" style={{ flex: '0 0 auto', marginLeft: 'auto' }} title={t('fees.remove')}
                  onClick={() => removeFee(f.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
        {/* Add a fee for one member */}
        <div className="rc-card-fields" data-fee-add style={{ marginTop: 10 }}>
          <select className="form-input" style={{ flex: '1 1 180px', minWidth: 0 }} value={pickMember}
            onChange={(e) => setPickMember(e.target.value)}>
            <option value="">{t('fees.pickMember')}</option>
            {availableMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button className="btn btn-small btn-secondary" disabled={!pickMember}
            onClick={() => { addFee(pickMember); setPickMember(''); }}>
            {t('fees.addFee')}
          </button>
        </div>
        {/* Apply the same fee to every active member on this show */}
        <div className="rc-card-fields" data-fee-bulk style={{ marginTop: 6 }}>
          <input className="form-input" style={{ flex: '0 1 120px', minWidth: 0 }} type="number" placeholder={t('fees.amount')}
            value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)} />
          <select className="form-input" style={{ flex: '0 1 90px', minWidth: 0 }} value={bulkCurrency}
            onChange={(e) => setBulkCurrency(e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button className="btn btn-small btn-secondary" disabled={bulkAmount === ''}
            onClick={() => {
              bulkFees({ all_on_show: true, amount: Number(bulkAmount) || 0, currency: bulkCurrency });
              setBulkAmount('');
            }}>
            {t('fees.applyToAll')}
          </button>
        </div>
        {/* Fee totals per currency — never converted/merged */}
        {Object.keys(feesByCurrency).length > 0 && (
          <div style={{ marginTop: 4, fontSize: '0.9rem', color: 'var(--text-dim)' }}>
            <strong style={{ color: 'var(--text)' }}>{t('fees.totals')}</strong>
            {Object.entries(feesByCurrency).map(([cur, total]) => (
              <div key={cur}>{t('fees.totalLine', { currency: cur, amount: total.toLocaleString() })}</div>
            ))}
          </div>
        )}
      </div>
      </>)}

      {/* Settlement — one line per currency, since we never convert. It mixes
          the fee and the expenses: shown to whoever holds either capability. */}
      {(canFees || canExpenses) && (
      <div className="rc-card col" data-finance="settlement" style={{ marginTop: 14 }}>
        <strong>{t('finance.settlement')}</strong>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.9rem' }}>
          <span>
            {t('finance.feeLine', { amount: (form.guarantee ?? 0).toLocaleString(), currency: showCurrency })}
          </span>
          {Object.entries(expensesByCurrency).map(([cur, total]) => (
            <span key={cur} style={{ color: 'var(--text-dim)' }}>
              {t('finance.expensesLine', { currency: cur, amount: total.toLocaleString() })}
            </span>
          ))}
          {Object.entries(feesByCurrency).map(([cur, total]) => (
            <span key={`fee-${cur}`} style={{ color: 'var(--text-dim)' }}>
              {t('fees.feesLine', { currency: cur, amount: total.toLocaleString() })}
            </span>
          ))}
          {perDiemTotal > 0 && (
            <span style={{ color: 'var(--text-dim)' }}>
              {t('finance.perDiemLine', { amount: perDiemTotal.toLocaleString(), currency: showCurrency })}
            </span>
          )}
          <span style={{ fontWeight: 600, marginTop: 4 }}>
            {t('finance.netLine', { currency: showCurrency, amount: netInDealCurrency.toLocaleString() })}
            {Object.keys(expensesByCurrency).some((c) => c !== (showCurrency)) && (
              <span style={{ color: 'var(--warning)', fontWeight: 400 }}>
                {t('finance.excludesOther')}
              </span>
            )}
          </span>
        </div>
      </div>
      )}
    </div>
  );
}
